/**
 * Item retornado pelas rotas do Instagram Graph API (hashtag/tags/mídias/stories)
 * ou sintetizado pelo fluxo de extração por URL (mapInstagramExtractItemsToRaw).
 * Não é um contrato fechado — a API e as etapas de enriquecimento no componente
 * (duração de vídeo, insights sintéticos para Mídias/Stories, etc.) adicionam
 * campos não listados aqui, por isso a index signature.
 */
export interface InstagramGalleryItem {
  id?: string;
  media_type?: string;
  caption?: string;
  media_url?: string;
  thumbnail_url?: string;
  permalink?: string;
  timestamp?: string;
  children?: { data?: InstagramGalleryItem[] };
  is_own_media?: boolean;
  insights?: Record<string, unknown>;
  duration?: number;
  shortcode?: string;
  code?: string;
  [key: string]: unknown;
}

/** Predicado único de filtro por tipo de mídia — substitui as 7 cópias duplicadas. */
export function matchesMediaType(
  item: InstagramGalleryItem,
  selectedTypes: readonly string[]
): boolean {
  return !selectedTypes.length || selectedTypes.includes(item.media_type ?? '');
}

/** Remove querystring, fragmento e barra final de uma URL de permalink. */
export function normalizePermalink(url?: string): string | undefined {
  if (!url) return undefined;
  const withoutFragment = url.split('#')[0];
  const withoutQuery = withoutFragment.split('?')[0];
  const withoutTrailingSlash = withoutQuery.endsWith('/') ? withoutQuery.slice(0, -1) : withoutQuery;
  return withoutTrailingSlash || undefined;
}

function nonEmpty(value?: string | null): string | undefined {
  return value && value.length > 0 ? value : undefined;
}

/**
 * Chave de identidade de um item, por prioridade: id → permalink normalizado
 * → shortcode → code. String vazia nunca é tratada como identidade válida em
 * nenhuma etapa; se nenhuma delas render uma chave, retorna undefined — nesse
 * caso o item é sempre tratado como único por mergeUniqueItems.
 */
export function getInstagramItemKey(item: InstagramGalleryItem): string | undefined {
  return (
    nonEmpty(item.id) ??
    normalizePermalink(item.permalink) ??
    nonEmpty(item.shortcode) ??
    nonEmpty(item.code)
  );
}

/**
 * Combina `current` + `incoming` preservando a ordem de `current` e anexando
 * apenas os itens de `incoming` cuja chave (getInstagramItemKey) ainda não
 * apareceu — nem em `current`, nem em itens de `incoming` já anexados nesta
 * mesma chamada. Itens sem chave identificável nunca são deduplicados.
 */
export function mergeUniqueItems<T extends InstagramGalleryItem>(
  current: readonly T[],
  incoming: readonly T[]
): T[] {
  const seen = new Set<string>();
  for (const item of current) {
    const key = getInstagramItemKey(item);
    if (key !== undefined) seen.add(key);
  }

  const merged = [...current];
  for (const item of incoming) {
    const key = getInstagramItemKey(item);
    if (key === undefined) {
      merged.push(item);
      continue;
    }
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(item);
  }
  return merged;
}
